/**
 * A fee per delivered job, a share of its order value kept between a floor
 * and a ceiling, instead of a fixed monthly price.
 */
export interface PlatformFeeSettings {
  feePercent: number
  minFee: number
  maxFee: number
  /** Most a store pays in a month; 0 means no cap. */
  monthlyCap: number
  /** Today's monthly price, which the fee is compared with. */
  currentMonthlyPrice: number
  /** A store counts from its first month with at least this many jobs. */
  minJobsPerMonth: number
}

export type PlatformFeeSettingsOverrides = Partial<PlatformFeeSettings>

export const PLATFORM_FEE_EXCLUSION_REASON = {
  /** Most jobs have no order value, so the fee can't be estimated. */
  MISSING_ORDER_VALUES: "MISSING_ORDER_VALUES",
  /** Too few months since the store started using Julo for real. */
  SHORT_HISTORY: "SHORT_HISTORY",
} as const

export type PlatformFeeExclusionReason =
  (typeof PLATFORM_FEE_EXCLUSION_REASON)[keyof typeof PLATFORM_FEE_EXCLUSION_REASON]

export interface PlatformFeeStoreMonth {
  /** "YYYY-MM" */
  month: string
  jobs: number
  /** The fee after the monthly cap. */
  fee: number
  /** Before the store started, estimated from its other months. */
  isEstimated: boolean
}

export interface PlatformFeeStore {
  storeId: string
  name: string
  /** "YYYY-MM", the first month with enough jobs. */
  startMonth: string
  activeMonths: number
  jobs: number
  jobsPerMonth: number
  /** The store's part of the order value, without the fee, averaged over the jobs with an order value. */
  avgOrderValue: number | null
  zeroValueJobs: number
  /** Average fee per job before the monthly cap. */
  feePerJob: number
  /** Average over a whole year, with the months before the start estimated. */
  estimatedMonthlyFee: number
  differenceFromCurrent: number
  months: PlatformFeeStoreMonth[]
  /** Why the store is left out of the totals, or null when it is counted. */
  exclusionReason: PlatformFeeExclusionReason | null
}

export interface PlatformFeeMonthTotal {
  /** "YYYY-MM" */
  month: string
  fee: number
  currentRevenue: number
}

export interface PlatformFeeTotals {
  storeCount: number
  estimatedMonthlyFee: number
  currentMonthlyRevenue: number
  difference: number
  storesPayingMore: number
  storesPayingLess: number
  feePerJob: number
}

export interface PlatformFeeSimulationResponse {
  settings: PlatformFeeSettings
  /** "YYYY-MM", the first month of the simulated year. */
  startMonth: string
  /** "YYYY-MM", the last month of the simulated year. */
  endMonth: string
  /** Busiest stores first. */
  stores: PlatformFeeStore[]
  totals: PlatformFeeTotals
  /** The counted stores' fees per month over the simulated year. */
  monthTotals: PlatformFeeMonthTotal[]
}
